import {createReducer} from '@reduxjs/toolkit';
import * as searchActions from '@src/actions/search.js';
import hasUnlimitedDate from '@src/utils/checkForNoEndDate.js';
import {dateRangesToISO} from '@src/utils/filters.js';
import {createGeometryObj} from '@src/utils/geom.js';
import getDateRanges from '@src/utils/getDateRanges.js';

const resetState = {
  dailyEmailEnabled: false,
  geometry: null,
  geometryZoomTo: true,
  id: null,
  name: '',
  noEndDate: false,
  page: 0,
  singleItemSearchId: null,
  featureReference: null,
};

export const initialState = {
  dateRanges: [],
  maxVertexCount: 1500,
  ...resetState,
};

const search = createReducer(initialState, builder => {
  builder.addCase(searchActions.saveGeometry, (state, {payload}) => {
    const {geometry, geometryZoomTo, name, featureReference} = payload;

    state.geometry = geometry;
    state.geometryZoomTo = geometryZoomTo;
    state.id = initialState.id;
    state.name = name;
    state.singleItemSearchId = initialState.singleItemSearchId;
    state.featureReference = featureReference;
  });

  builder.addCase(
    searchActions.setDateRanges,
    (state, {payload: dateRanges}) => {
      state.dateRanges = dateRangesToISO(dateRanges);
    },
  );

  builder.addCase(searchActions.continueSearch, state => {
    state.page = state.page + 1;
  });

  builder.addCase(searchActions.loadSearch, (state, {payload: search}) => {
    const dateRanges = getDateRanges(search.filter);

    state.dailyEmailEnabled = search.__daily_email_enabled;
    state.id = search.id;
    state.dateRanges = dateRangesToISO(dateRanges);
    state.noEndDate = hasUnlimitedDate(dateRanges);
    state.name = search.name;
    state.singleItemSearchId = initialState.singleItemSearchId;
    state.featureReference = search.featureReference;
  });

  builder.addCase(searchActions.searchSingleItem, (state, {payload: item}) => {
    state.name = item.id;
    state.singleItemSearchId = item.id;
    state.featureReference = initialState.featureReference;
  });

  builder.addCase(searchActions.clearSearch, state => {
    return {
      ...state,
      ...resetState,
    };
  });

  builder.addCase(searchActions.resetDateRanges, state => {
    state.dateRanges = initialState.dateRanges;
    state.noEndDate = initialState.noEndDate;
  });

  builder.addCase(searchActions.toggleNoEndDate, (state, {payload}) => {
    state.noEndDate = typeof payload === 'boolean' ? payload : !state.noEndDate;
  });

  builder.addCase(searchActions.setIsGeometryTooComplex, (state, {payload}) => {
    state.isGeometryTooComplex = payload;
  });

  builder.addCase(searchActions.resetSearchPageNumber, state => {
    state.page = initialState.page;
  });

  builder.addCase(
    searchActions.executeSearch,
    (state, {payload: suggestion}) => {
      state.id = initialState.id;
      state.page = initialState.page;
      state.singleItemSearchId = initialState.singleItemSearchId;
      state.dailyEmailEnabled = false;
      state.featureReference = initialState.featureReference;

      if (suggestion) {
        const {newGeometry} = createGeometryObj(suggestion.geometry);

        state.geometry = newGeometry;
        state.geometryId = state.geometryId === null ? 1 : state.geometryId + 1;
        state.geometryZoomTo = true;
        state.name = suggestion.label || suggestion.title;
      }
    },
  );
});

export default search;
